/**
 * MEASURED electrode resistance on the classic 4-channel BrainBit over raw BLE
 * (V1-CLOSURE R5, ruling R70). The driver (`brainbitClassic.ts`) runs this
 * during fitting only; the signal-derived estimate (`signalQuality.ts`) is its
 * automatic fallback. `docs/classic-resistance.md` is the prose version.
 *
 * ── THE PROTOCOL IS THE VENDOR'S ────────────────────────────────────────────
 *
 * Reimplemented here, never depended on, from BrainBit's own Web Bluetooth
 * library `web-neurosdk-brainbit` 2.1.0 (npm; licence ISC per its package.json,
 * the tarball carries no LICENSE file; tarball sha256 d32bff7d…dd43442). Line
 * numbers are that release's `src/`:
 *
 *   lib/static.js               6–29    commands: stop `01`, signal `02 00 00 00 00`,
 *                                        resist contact 1–4 × direct / reversed
 *                               31–75   status codes, command-error codes
 *   lib/brainbit-utils.js       13–78   the packet arithmetic, in volts (the multiplier
 *                                        at 101–107 is the same in signal and resist mode)
 *                               80–88   a status frame is [status, cmdError, battery, firmware]
 *                               109–110 getStartResistCommand(contact, reversed)
 *   lib/brainbit-subscribes.js  63–127  the cycle, below
 *                               129–134 calculateResistance → Ω
 *                               136–139 isInfinity: |value| > 0.75
 *   brainbit.js                 97–102  stopResistanceData writes stop
 *                               104–120 startResistanceData writes resist contact 1 direct
 *
 * The same service, characteristics, STOP and START as the signal path, which
 * came from `calibrations/backend/_reference/brainbit_bleak_adapter.py`.
 *
 * ── THE CYCLE ───────────────────────────────────────────────────────────────
 *
 * The headband measures one contact and one polarity at a time. A resist
 * command names a contact (1–4 in BLE order: O1, T3, T4, O2 — the order of the
 * signal packet) and a polarity (direct, reversed). The status characteristic
 * answers; a frame whose status is RESIST (3) arms the cycle, any other status
 * disarms it and sends it back to contact 1 direct. While armed, every RX frame
 * whose two values on the active contact are both non-zero adds them, ×2, to an
 * accumulator; at 26 values, values 20–24 are kept for that contact and
 * polarity, the cycle disarms, advances (direct → reversed → next contact
 * direct) and sends the next command. After contact 4 reversed, one reading
 * per contact:
 *
 *   R [Ω] = |mean(direct) − mean(reversed)| / 150e-9 / 4,   or +∞ when a kept value is beyond ±0.75.
 *
 * ── WHERE THIS DIFFERS FROM THE VENDOR, AND WHY ─────────────────────────────
 *
 *   1. isInfinity is called with `[...pos, neg]` (subscribes.js:96–99): the
 *      reversed values are not spread, an array is compared with 0.75, and
 *      they are never checked. This checks both — what the line evidently
 *      means. It can only turn a reading that railed on the reversed polarity
 *      into "no reading" (No contact) instead of a finite figure. The fixture
 *      `reversedRail` pins both answers.
 *   2. The cycle starts DISARMED and waits for the headband's RESIST status
 *      (the vendor's flag starts true). Frames from before the headband
 *      answered are never counted.
 *   3. Anything the vendor would choke on or wait on forever is an ERROR here,
 *      so the driver can fall back: a status of INVALID or BOOTLOADER_NEED
 *      (which the vendor's startResistanceData refuses to start from), a status
 *      the vendor does not list, a command error on a frame that did not reach
 *      RESIST, an empty status frame, and a short RX frame while armed.
 */

import { HeadbandError } from './types'

/** Resist contacts 1–4, in BLE order: the signal packet's O1, T3, T4, O2. */
export const RESIST_CONTACTS = ['O1', 'T3', 'T4', 'O2'] as const
export type ResistContact = (typeof RESIST_CONTACTS)[number]
type ContactNo = 1 | 2 | 3 | 4

/** `lib/static.js` 10–27, byte for byte. */
export const RESIST_COMMANDS: Readonly<Record<ContactNo, { direct: readonly number[]; reversed: readonly number[] }>> = Object.freeze({
  1: { direct: [3, 48, 145, 145, 145, 1, 1, 0], reversed: [3, 48, 145, 145, 145, 1, 1, 255] },
  2: { direct: [3, 145, 48, 145, 145, 2, 2, 0], reversed: [3, 145, 48, 145, 145, 2, 2, 255] },
  3: { direct: [3, 145, 145, 48, 145, 4, 4, 0], reversed: [3, 145, 145, 48, 145, 4, 4, 255] },
  4: { direct: [3, 145, 145, 145, 48, 8, 8, 0], reversed: [3, 145, 145, 145, 48, 8, 8, 255] },
})

/** `getStartResistCommand` (`lib/brainbit-utils.js` 109–110). */
export function resistCommand(contact: ContactNo, reversed: boolean): Uint8Array {
  return new Uint8Array(RESIST_COMMANDS[contact][reversed ? 'reversed' : 'direct'])
}

/** The status characteristic's first byte (`lib/static.js` 31–57). */
export const STATUS = Object.freeze({ INVALID: 0, STOPPED: 1, SIGNAL: 2, RESIST: 3, BOOTLOADER_NEED: 4 })
/** Its second byte (`lib/static.js` 59–75). */
export const CMD_ERROR = Object.freeze({ NONE: 0, LENGTH: 1, MODE: 2 })

/** ADC units → volts, in signal and in resist mode alike (`lib/brainbit-utils.js` 103–105). */
export const VOLTS_PER_UNIT = 2.4 / (0xfffff * 6)
/** `lib/brainbit-subscribes.js` 68, 88, 90, 92, 133, 137. */
export const RESIST_MULTIPLIER = 2
export const VALUES_PER_STEP = 26
export const KEEP_FROM = 20
export const KEEP_TO = 25
export const PROBE_CURRENT_A = 150e-9
export const PROBE_DIVISOR = 4
export const OPEN_LIMIT = 0.75

function bytesOf(data: Uint8Array | DataView): Uint8Array {
  return data instanceof DataView ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : data
}

export interface StatusFrame {
  status: number
  /** null when the frame is too short to carry one. */
  cmdError: number | null
}

/** `decodeStatusResponse` (`lib/brainbit-utils.js` 80–88), the two bytes this needs. */
export function decodeStatusFrame(data: Uint8Array | DataView): StatusFrame {
  const d = bytesOf(data)
  if (d.length === 0) throw new HeadbandError('protocol', 'an empty status frame')
  return { status: d[0]!, cmdError: d.length > 1 ? d[1]! : null }
}

export type Quad = [number, number, number, number]

/**
 * A 20-byte RX frame's eight values in VOLTS, BLE order (contact 1–4), exactly
 * the vendor's `parseSignalValuesShifts` (`lib/brainbit-utils.js` 13–78):
 * JavaScript int32 shifts, divided, then scaled, with no rounding. The signal
 * path's `decodeBrainBitPacket` is the same arithmetic in µV, reordered and
 * rounded, and is left exactly as it was.
 */
export function decodeRawVolts(data: Uint8Array | DataView): { first: Quad; second: Quad } {
  const d = bytesOf(data)
  if (d.length < 20) throw new HeadbandError('protocol', `expected a 20-byte packet, got ${d.length}`)
  const b = (i: number) => d[i]!
  const v00 = (((b(1) & 0x0f) << 28) | (b(2) << 20) | (b(3) << 12) | (b(4) << 4)) / 2048
  const v01 = (((b(4) & 0x7f) << 25) | (b(5) << 17) | (b(6) << 9) | (b(7) << 1)) / 2048
  const v02 = (((b(6) & 0x03) << 30) | (b(7) << 22) | (b(8) << 14) | (b(9) << 6)) / 2048
  const v03 = (((b(9) & 0x1f) << 27) | (b(10) << 19) | (b(11) << 11)) / 2048
  const v10 = ((b(12) << 24) | (b(13) << 16)) / 65536 + v00
  const v11 = ((b(14) << 24) | (b(15) << 16)) / 65536 + v01
  const v12 = ((b(16) << 24) | (b(17) << 16)) / 65536 + v02
  const v13 = ((b(18) << 24) | (b(19) << 16)) / 65536 + v03
  const m = VOLTS_PER_UNIT
  return { first: [v00 * m, v01 * m, v02 * m, v03 * m], second: [v10 * m, v11 * m, v12 * m, v13 * m] }
}

/** `calculateResistance` (`lib/brainbit-subscribes.js` 129–134), in Ω. */
export function ohmsFrom(direct: readonly number[], reversed: readonly number[]): number {
  return (
    Math.abs(direct.reduce((a, b) => a + b, 0) / (direct.length || 1) - reversed.reduce((a, b) => a + b, 0) / (reversed.length || 1)) /
    PROBE_CURRENT_A /
    PROBE_DIVISOR
  )
}

/** `isInfinity` (`lib/brainbit-subscribes.js` 136–139): a kept value beyond ±0.75. */
export function railed(values: readonly number[]): boolean {
  return values.some((v) => v > OPEN_LIMIT || v < -OPEN_LIMIT)
}

export interface CycleOutput {
  /** The next resist command to write, when a step has just completed. */
  command?: Uint8Array
  /** A full reading, Ω per contact; +∞ = not measurable (no reading). */
  ohms?: Record<ResistContact, number>
}

/** The vendor's resistance cycle as a pure state machine: bytes in, commands and ohms out. */
export class ResistanceCycle {
  private contact: ContactNo = 1
  private reversed = false
  private armed = false
  private acc: number[] = []
  private readonly direct: number[][] = [[], [], [], []]
  private readonly rev: number[][] = [[], [], [], []]

  /** The command that starts a measurement: contact 1, direct (`brainbit.js` 112). */
  static first(): Uint8Array {
    return resistCommand(1, false)
  }

  /** Feed a status frame. Throws a `HeadbandError` when the headband refused or cannot measure. */
  onStatus(data: Uint8Array | DataView): void {
    const f = decodeStatusFrame(data)
    if (f.status === STATUS.INVALID) throw new HeadbandError('protocol', 'the headband reports its service is not initialised')
    if (f.status === STATUS.BOOTLOADER_NEED) throw new HeadbandError('protocol', 'the headband reports it needs its bootloader')
    if (f.status > STATUS.BOOTLOADER_NEED) throw new HeadbandError('protocol', `an unknown headband status ${f.status}`)
    if (f.status === STATUS.RESIST) {
      this.armed = true
      return
    }
    if (f.cmdError !== null && f.cmdError !== CMD_ERROR.NONE) {
      throw new HeadbandError('protocol', `the headband refused the resistance command (status ${f.status}, error ${f.cmdError})`)
    }
    // `lib/brainbit-subscribes.js` 73–77: any other status disarms and starts over.
    this.armed = false
    this.contact = 1
    this.reversed = false
  }

  /** Feed an RX frame. Throws a `HeadbandError` on a short frame while armed. */
  onPacket(data: Uint8Array | DataView): CycleOutput {
    if (!this.armed) return {}
    const { first, second } = decodeRawVolts(data)
    const i = this.contact - 1
    const v0 = first[i]!
    const v1 = second[i]!
    if (v0 === 0 || v1 === 0) return {}
    this.acc.push(v0 * RESIST_MULTIPLIER, v1 * RESIST_MULTIPLIER)
    if (this.acc.length !== VALUES_PER_STEP) return {}
    const kept = this.acc.slice(KEEP_FROM, KEEP_TO)
    if (this.reversed) this.rev[i] = kept
    else this.direct[i] = kept
    this.acc = []
    const out: CycleOutput = {}
    if (this.contact === 4 && this.reversed) {
      const ohms = {} as Record<ResistContact, number>
      RESIST_CONTACTS.forEach((name, k) => {
        const d = this.direct[k]!
        const r = this.rev[k]!
        ohms[name] = railed(d) || railed(r) ? Number.POSITIVE_INFINITY : ohmsFrom(d, r)
      })
      out.ohms = ohms
    }
    if (this.reversed) this.contact = this.contact >= 4 ? 1 : ((this.contact + 1) as ContactNo)
    this.reversed = !this.reversed
    this.armed = false
    out.command = resistCommand(this.contact, this.reversed)
    return out
  }
}
